/**
 * Page « Mes factures » du dashboard client (/dashboard/factures).
 *
 * Liste tabulaire de toutes les factures du user, joignant les paiements
 * validés à leurs projets. Téléchargement PDF via presigned URL S3.
 */
import { useQuery } from '@tanstack/react-query';
import { Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { extractErrorMessage } from '@/features/auth/error-message';
import {
  getInvoiceDownloadUrl,
  listMineInvoices,
  type MineInvoice,
} from '@/features/payments/invoices-api';

function formatEuros(cents: number): string {
  return (cents / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  });
}

export function DashboardFacturesPage() {
  const q = useQuery({ queryKey: ['invoices', 'mine'], queryFn: listMineInvoices });

  async function handleDownload(invoice: MineInvoice) {
    try {
      const url = await getInvoiceDownloadUrl(invoice.id);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  const list = q.data ?? [];

  return (
    <DashboardLayout title="Mes factures">
      <Card>
        <CardHeader>
          <CardTitle>Factures</CardTitle>
          <CardDescription>
            Téléchargez vos factures au format PDF.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : list.length === 0 ? (
            <div className="rounded-md border bg-white p-6 text-center text-sm text-gray-500">
              Aucune facture pour le moment.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Dossier</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Méthode</TableHead>
                  <TableHead>Montant HT</TableHead>
                  <TableHead>Montant TTC</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <FileText className="h-4 w-4 text-gray-400" />
                        {inv.number}
                      </span>
                    </TableCell>
                    <TableCell>{inv.projectReference}</TableCell>
                    <TableCell>
                      {new Date(inv.issuedAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell className="capitalize">
                      {inv.paymentMethod}
                    </TableCell>
                    <TableCell>{formatEuros(inv.amountHtCents)}</TableCell>
                    <TableCell className="font-medium">
                      {formatEuros(inv.amountTtcCents)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(inv)}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
